// Purchase Orders — supplier-facing. Same shape as Quotes (line items in
// jsonb), different status lifecycle (draft → sent → received | cancelled).
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useToast, ToastBanner } from '../../components/Toast'
import { apiFetch } from '../../lib/api'
import LineItemEditor, { LineItemDraft, lineItemsTotal } from './LineItemEditor'

interface PO {
  id: string; supplierName: string; supplierEmail: string | null
  status: string; totalCents: number
  expectedAt: string | null; receivedAt: string | null; notes: string | null
  lineItems: Array<LineItemDraft & { lineTotalCents?: number }>
  createdAt: string
}

const STATUS_TONE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-800',
  received: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-700',
}

export default function PurchaseOrders() {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<PO[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PO | null>(null)
  const [draft, setDraft] = useState<{ supplierName: string; supplierEmail: string; expectedAt: string; notes: string; lineItems: LineItemDraft[] }>({ supplierName: '', supplierEmail: '', expectedAt: '', notes: '', lineItems: [] })
  const { toast, show } = useToast()

  async function load() {
    try {
      const d = await apiFetch<{ items: PO[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/purchase-orders')
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  function openCreate() { setDraft({ supplierName: '', supplierEmail: '', expectedAt: '', notes: '', lineItems: [] }); setCreating(true) }
  function openEdit(p: PO) {
    setDraft({
      supplierName: p.supplierName,
      supplierEmail: p.supplierEmail ?? '',
      expectedAt: p.expectedAt ?? '',
      notes: p.notes ?? '',
      lineItems: p.lineItems.map(({ description, qty, unitPriceCents }) => ({ description, qty, unitPriceCents })),
    })
    setEditing(p)
  }

  async function save() {
    if (!draft.supplierName) { alert('Supplier name required'); return }
    const body = { ...draft, supplierEmail: draft.supplierEmail || null, expectedAt: draft.expectedAt || null }
    try {
      if (editing) {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/purchase-orders/${editing.id}`, { method: 'PATCH', body: JSON.stringify(body) })
      } else {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/purchase-orders', { method: 'POST', body: JSON.stringify(body) })
      }
      setCreating(false); setEditing(null); await load()
      show(editing ? 'Purchase order updated.' : 'Purchase order created.')
    } catch (e) { show((e as Error).message, 'error', 5000) }
  }
  async function setStatus(id: string, status: string) {
    // Same pattern as Quotes: confirm the commit-style transitions
    // (sent to supplier, cancelled) since they're easy to mis-click in
    // a dropdown and hard to walk back without an audit log.
    const needsConfirm = status === 'sent' || status === 'cancelled'
    if (needsConfirm && !confirm(`Set this PO to "${status}"? This is hard to reverse cleanly.`)) {
      await load()
      return
    }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/purchase-orders/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) })
      await load()
    } catch (e) { alert((e as Error).message) }
  }
  async function remove(id: string) {
    if (!confirm('Delete this PO?')) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/purchase-orders/${id}`, { method: 'DELETE' })
      await load()
    } catch (e) { alert((e as Error).message) }
  }

  const isModalOpen = creating || !!editing

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="eyebrow">Processing</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Purchase Order</h1>
        </div>
        <button onClick={openCreate} className="btn-gold">+ New PO</button>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">PO</th>
              <th className="text-left px-4 py-2 font-bold">Supplier</th>
              <th className="text-left px-4 py-2 font-bold">Status</th>
              <th className="text-right px-4 py-2 font-bold">Total</th>
              <th className="text-right px-4 py-2 font-bold">Expected</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No purchase orders.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3"><button onClick={() => openEdit(p)} className="font-mono text-xs text-ink-2 hover:text-gold-dark">{p.id}</button></td>
                <td className="px-4 py-3"><div className="text-ink">{p.supplierName}</div><div className="text-xs text-ink-3">{p.supplierEmail ?? '—'}</div></td>
                <td className="px-4 py-3">
                  <select value={p.status} onChange={(e) => setStatus(p.id, e.target.value)}
                    className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border-0 ' + (STATUS_TONE[p.status] ?? 'bg-gray-100 text-gray-700')}>
                    <option value="draft">draft</option><option value="sent">sent</option><option value="received">received</option><option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-ink">${(p.totalCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{p.expectedAt ?? '—'}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(p.id)} className="text-ink-3 hover:text-red-600">×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading uppercase font-bold text-lg text-ink mb-4">{editing ? `Edit PO ${editing.id.slice(0,8)}…` : 'New purchase order'}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div><div className="eyebrow">Supplier name</div><input value={draft.supplierName} onChange={(e) => setDraft({ ...draft, supplierName: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Supplier email</div><input type="email" value={draft.supplierEmail} onChange={(e) => setDraft({ ...draft, supplierEmail: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Expected delivery</div><input type="date" value={draft.expectedAt} onChange={(e) => setDraft({ ...draft, expectedAt: e.target.value })} className="input" /></div>
            </div>
            <div className="mt-4"><div className="eyebrow">Line items</div>
              <LineItemEditor items={draft.lineItems} onChange={(li) => setDraft({ ...draft, lineItems: li })}
                footer={<div className="text-sm font-semibold text-ink">Total ${(lineItemsTotal(draft.lineItems) / 100).toFixed(2)}</div>} />
            </div>
            <div className="mt-3"><div className="eyebrow">Notes</div><textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="input" /></div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setCreating(false); setEditing(null) }} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={save} className="btn-gold flex-1 justify-center">{editing ? 'Save changes' : 'Create PO'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
